import React from "react";
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  KeyboardAvoidingView,
  Dimensions,
  StyleSheet,
  Platform,
} from "react-native";
import { omit } from "lodash";

import Utils, {
  hasItems,
  isAndroid,
  isWeb,
  navigate,
} from "../../../../helpers/utils";
import appStyles from "../../../appStyles";

import Search from "../../common/search-bar/Search";
import IntegrationWrapper from "../details/IntegrationWrapper";
import IntegrationsHelper from "../IntegrationsHelper";
import Header from "../../common/header/index";
import IntegrationsMappingFabButton from "./IntegrationsMappingFabButton";
import MappingScore from "./MappingScore";
import ElementsList from "../../elements/list/ElementsList";
import ElementsHelper from "../../elements/ElementsHelper";
import appPages from "../../../../appPages";
import SettingsIcon from "../../common/icons/more/SettingsIcon";
import Icons from "../../common/icons/index";
import AppTheme from "../../AppTheme";
import IntegrationMappingEmpty from "./IntegrationMappingEmpty";
import IntegrationsMappingDone from "./IntegrationsMappingDone";
import LoadingIndicator from "../../common/LoadingIndicator";
import ItemNotFound from "../../common/ItemNotFound";

const { height: HEIGHT } = Dimensions.get("window");
const NUMBER_MATCHES = 10;

export default class IntegrationsMappingGame extends React.Component {
  constructor(props) {
    super(props);
    this.controller = props.controller;
    this.searchRef = null;

    const integration = this.props.route.params?.integration;
    const skipActivity = this.props.route.params?.skipActivity;
    const allowSingleMatch = this.props.route.params?.allowSingleMatch; // from AutotrackMappingCard

    this.state = {
      elements: [],
      integrations: [],
      index: 0,
      selectedIntegration: integration,
      skipActivity,
      allowSingleMatch,
      counter: 0,
      hasEnabledIntegrations: false,
      isLoading: true,
      isMappingDone: false,
    };
  }

  componentDidMount = async () => {
    if (this.controller.hasNavigation()) {
      this.focusListenerUnsub = this.props.navigation.addListener(
        "focus",
        () => {
          this.fetchData();
        },
      );
    } else {
      this.fetchData({});
    }
  };

  componentWillUnmount = async () => {
    if (this.focusListenerUnsub) this.focusListenerUnsub();
  };

  fetchData = async () => {
    const { selectedIntegration, skipActivity } = this.state;

    const hasEnabledIntegrations = IntegrationsHelper.hasEnabledIntegrations(
      this.controller,
    );

    // New element created from Mari
    const element = this.props.route.params?.element;
    if (element) {
      this.onElementSelected(omit(element, ["integration"]));
      this.props.navigation?.setParams({ element: null });
      return;
    }

    this.setState((state) => ({
      ...state,
      counter: 0,
      hasEnabledIntegrations,
    }));

    // Coming from mapping card
    if (selectedIntegration && !skipActivity) {
      this.setState((state) => ({
        ...state,
        integrations: [selectedIntegration],
        index: 0,
        isLoading: false,
      }));
    } else {
      this.getIntegrations();
    }

    // Sync with server
    this.controller.getApiData("elements", {}, (elements) => {
      this.setState((state) => ({
        ...state,
        elements: ElementsHelper.sortElements(elements, "is_major|is_core"),
      }));
    });
  };

  getIntegrations = () => {
    const { selectedIntegration } = this.state;

    this.setState((state) => ({
      ...state,
      isLoading: true,
    }));

    IntegrationsHelper.getIntegrationsMapping(this.controller).then(
      (integrations) => {
        const newIntegrations =
          hasItems(integrations) && selectedIntegration
            ? integrations.filter((int) => int.id != selectedIntegration.id)
            : integrations;

        if (hasItems(newIntegrations)) {
          this.setState({
            integrations: newIntegrations,
            index: 0,
            isLoading: false,
            isMappingDone: false,
          });
        } else {
          this.setState((state) => ({
            ...state,
            isLoading: false,
            // User finished all available mapping elements
            isMappingDone: true,
          }));
        }
      },
    );
  };

  goBack = () => {
    this.controller.goBack();
  };

  onElementSelected = (element) => {
    const { integrations, index, counter, allowSingleMatch } = this.state;

    const integration =
      integrations && index < integrations.length ? integrations[index] : null;

    if (integration) {
      const operator = "match";
      const discriminator = "";

      IntegrationsHelper.mapIntegrationToElement(
        this.controller,
        {
          operator,
          discriminator,
          integration,
          element,
        },
        () => {
          if (allowSingleMatch) {
            this.controller.navigate(appPages.homePage);
          } else if (counter >= NUMBER_MATCHES - 1) {
            this.setState((state) => ({ ...state, isMappingDone: true }));
          } else {
            // Reset search
            if (this.searchRef) this.searchRef.clearSearch();
            this.setState((state) => ({
              ...state,
              index: index + 1,
              counter: counter + 1,
            }));
          }
        },
      );
    }
  };

  // ------------------------------------- FILTER -------------------------------------
  filterByKeyword = ({ term }) => {
    const elements = ElementsHelper.filterResults(this.controller, {
      term,
      sortBy: "relevance",
    });

    this.setState((state) => ({
      ...state,
      isLoading: false,
      elements,
      searchTerm: term,
    }));
  };

  goToIntegrations = () => {
    navigate(this.controller, appPages.integrationsV2);
  };

  onPrevious = () => {
    const { index } = this.state;

    this.setState((state) => ({
      ...state,
      index: index > 1 ? index - 1 : 0,
    }));
  };

  onSkip = () => {
    const { index, integrations } = this.state;

    if (integrations && index < integrations.length - 1) {
      this.setState((state) => ({ ...state, index: index + 1 }));
    } else {
      this.getIntegrations();
    }
  };

  addNewElement = (name = "") => {
    const { integrations, index } = this.state;

    const integration =
      integrations && index < integrations.length ? integrations[index] : null;

    if (!integration) return;

    let model = Utils.getElementModel({ name });
    model.integration = integration;

    this.controller.changePage("feed", {
      selectedPage: `edit_element`,
      args: {
        element: model,
      },
    });
  };

  matchMore = () => {
    this.getIntegrations();
  }

  // ------------------------------------- RENDER -------------------------------------
  render() {
    const {
      integrations,
      index,
      counter,
      elements,
      isLoading,
      mariMessage,
      hasEnabledIntegrations,
      isMappingDone,
      allowSingleMatch,
      searchTerm,
    } = this.state;

    const hasPrevious = index > 0;

    const integration =
      integrations && index < integrations.length ? integrations[index] : null;

    const finishedMapping = isMappingDone || !integration;

    return (
      <>
        {isLoading ? (
          <LoadingIndicator isLoading={true} />
        ) : hasEnabledIntegrations && finishedMapping ? (
          <IntegrationsMappingDone
            isMappingDone={finishedMapping}
            matchMore={this.matchMore}
            controller={this.controller}
          />
        ) : (
          <View style={[appStyles.container, { backgroundColor: "#FFF" }]}>
            <KeyboardAvoidingView
              style={appStyles.kbViewNoFooter}
              behavior="position"
              keyboardVerticalOffset={isAndroid ? 30 : 0}
            >
              <Header
                title="Let's get specific"
                subTitle="Help us understand your time better"
                isLoading={isLoading}
                goBack={this.goBack}
                iconTag={
                  <SettingsIcon
                    fillColor="#FFF"
                    onPress={this.goToIntegrations}
                  />
                }
              />

              {/* No integrations enabled */}
              {!hasEnabledIntegrations ? (
                <IntegrationMappingEmpty controller={this.controller} />
              ) : (
                <>
                  {/* Game components */}
                  <ScrollView
                    style={[
                      appStyles.scrollView,
                      {
                        paddingHorizontal: 0,
                      },
                    ]}
                    contentContainerStyle={{
                      paddingBottom: isWeb ? 80 : 260,
                    }}
                  >
                    {/* Score */}
                    <MappingScore count={counter} />

                    {/* Buttons */}
                    {allowSingleMatch ? null : (
                      <View style={styles.buttonsContainer}>
                        <TouchableOpacity
                          style={styles.arrowContainer}
                          onPress={this.onPrevious}
                        >
                          <Icons.ArrowLeft
                            onPress={this.onPrevious}
                            fillColor={
                              hasPrevious ? AppTheme.DEFAULT_COLOR : "#B0B0B0"
                            }
                          />
                          <Text
                            style={[
                              styles.arrowText,
                              !hasPrevious && { color: "#B0B0B0" },
                            ]}
                          >
                            Previous
                          </Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                          style={styles.arrowContainer}
                          onPress={this.onSkip}
                        >
                          <Text style={styles.arrowText}> Skip</Text>
                          <Icons.ArrowRight
                            onPress={this.onSkip}
                            fillColor={AppTheme.DEFAULT_COLOR}
                          />
                        </TouchableOpacity>
                      </View>
                    )}

                    {integration ? (
                      <IntegrationWrapper
                        controller={this.controller}
                        data={integration}
                        highlight={true}
                        style={{
                          minHeight: 40,
                          paddingLeft: 16,
                          paddingRight: 14,
                          paddingBottom: 0,
                        }}
                      />
                    ) : null}

                    {hasItems(elements) ? (
                      <>
                        <Text style={styles.elementsInfoText}>
                          Which Element best fits the activity above?
                        </Text>

                        <ElementsList
                          elements={elements}
                          initialNumToRender={10}
                          controller={this.controller}
                          onElementSelected={this.onElementSelected}
                          noIcons={true}
                          hideActions={true}
                          listStyle={{
                            maxHeight: HEIGHT - 300,
                          }}
                        />
                      </>
                    ) : searchTerm ? (
                      <ItemNotFound
                        name={searchTerm}
                        type="element"
                        create={() => this.addNewElement(searchTerm)}
                      />
                    ) : null}
                  </ScrollView>

                  <View style={[appStyles.footerOptions]}>
                    <IntegrationsMappingFabButton
                      addNewElement={this.addNewElement}
                      mariOptions={{
                        mariMessage,
                      }}
                      style={{
                        bottom: 42,
                      }}
                    />

                    <Search
                      ref={(ref) => {
                        this.searchRef = ref;
                      }}
                      placeholder="Search Elements or Create New"
                      filterResults={this.filterByKeyword}
                      controller={this.controller}
                    />
                  </View>
                </>
              )}
            </KeyboardAvoidingView>
          </View>
        )}
      </>
    );
  }
}

const styles = StyleSheet.create({
  buttonsContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: "row",
  },
  arrowContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "row",
    height: 40,
  },
  arrowText: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 12,
    lineHeight: 15,
    letterSpacing: 0.175,
    color: AppTheme.DEFAULT_COLOR,
  },
  elementsInfoText: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 12,
    lineHeight: 15,
    letterSpacing: 0.175,
    color: AppTheme.DEFAULT_COLOR,
    marginTop: 12,
    marginBottom: 8,
    textAlign: "center",
  },
});
